import * as React from 'react';
import {TicketDetailQuery, useTicketDetailFetchKey} from '../hooks/useTicketDetail';
import {ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {TicketDetailSkeleton} from '../components/TicketDetailSkeleton';
import {TicketNotFound} from '../components/TicketNotFound';
import {MainNarrow, RouteErrorBoundary, TicketDetailContent} from '../components/TicketDetailContent';
import type {useTicketDetailQuery$data} from '../hooks/__generated__/useTicketDetailQuery.graphql';

/**
 * The ticket detail page, fetched by raw ticket id.
 *
 * Only the query lives here — the page itself is `TicketDetailContent`, shared with the
 * slug route. Add UI there, not here, so both ways into a ticket stay the same page.
 */
export function TicketDetailByIdRoute({id}: {id: string}) {
	const {fetchKey, refetch} = useTicketDetailFetchKey();

	return (
		<RouteErrorBoundary key={fetchKey}>
			<ForecastQueryRenderer
				query={TicketDetailQuery}
				variables={{id}}
				customLoader={() => (
					<MainNarrow>
						<TicketDetailSkeleton />
					</MainNarrow>
				)}
				redirectIfNotLoggedIn
				render={(relayProps: useTicketDetailQuery$data | null) => {
					if (!relayProps || !relayProps.viewer) return null;
					const detail = relayProps.viewer.ticketDetail;
					if (!detail) {
						return (
							<MainNarrow>
								<TicketNotFound />
							</MainNarrow>
						);
					}
					return (
						<TicketDetailContent
							ticketRef={detail.ticket}
							timeline={detail.timeline}
							attachments={detail.attachments}
							refetch={refetch}
							currentPersonId={relayProps.viewer.actualPersonId ?? ''}
						/>
					);
				}}
			/>
		</RouteErrorBoundary>
	);
}
